import { Box, Button, Divider, IconButton, Stack, Tooltip, Typography } from "@material-ui/core";
import { ContactTableAvatar } from "./ContactTableAvatar";
import { ContactTablePhoneNumber } from "./ContactTablePhoneNumber";
import { ContactTableNestedRowDetails } from "./ContactTableNestedRowDetails";
import { ContactTableAdditionalNotes } from "./ContactTableAdditionalNotes";
import { ContactTableNestedRowSharedComments } from "./ContactTableNestedRowSharedComments";
import { ContactTableNestedRowEvents } from "./ContactTableNestedRowEvents";
import { QuickNotesInline } from "components/_dashboard/rentalToSale/QuickNotesInline";
import useFirestore from "hooks/useFirestore";
import { Call, Edit, WhatsApp } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { getContactCrmIsHot } from "../../../../../@types/sharedSchema";
import type { RealEstateContact } from "../../../../../@types/sharedSchema";

export type ContactTableNestedRowGeneralProps = {
  contact: RealEstateContact;
  handleEditContactDialogOpen: () => void;
};

export function ContactTableNestedRowGeneral({
  contact,
  handleEditContactDialogOpen
}: ContactTableNestedRowGeneralProps) {
  const { organizationId } = useFirestore();

  const { t: translate } = useTranslation();

  const { callerLastName, callerFirstName } = contact;
  // The document id IS the contact's phone (see `contactRekey.ts`). `callerPhoneNumber`
  // is a legacy mirror: absent on ~90% of documents and, where present, sometimes a
  // local-format string that agrees with nothing.
  const contactPhone = contact.id;

  const callerFullName = [callerFirstName, callerLastName].filter(Boolean).join(" ").trim() || "-";

  return (
    <Stack gap={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems={{ xs: "normal", sm: "start" }}
      >
        <Box>
          <Stack direction="row" gap={2}>
            <ContactTableAvatar
              badgeSize={24}
              avatarSize={56}
              callerFullName={callerFullName}
              crmIsHot={getContactCrmIsHot(contact)}
            />
            <Stack>
              <Typography variant="h5">{callerFullName}</Typography>
              <ContactTablePhoneNumber contact={contact} />
            </Stack>
          </Stack>
        </Box>
        <Stack direction="row">
          <Tooltip title={translate("contactsPage.control.edit")} arrow>
            <IconButton onClick={handleEditContactDialogOpen}>
              <Edit />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <ContactTableNestedRowDetails contact={contact} />
      <ContactTableAdditionalNotes contact={contact} hideInputWhenEmpty />
      {contact.id && <QuickNotesInline contactId={contact.id} />}
      {organizationId && <ContactTableNestedRowSharedComments contact={contact} />}
      <ContactTableNestedRowEvents contact={contact} />
      <Divider />
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}>
        <Box>
          <Typography variant="h4">{callerFullName}</Typography>
        </Box>
        <Stack direction={{ xs: "row", sm: "column" }} justifyContent="center" spacing={1}>
          <Button
            href={`tel:${contactPhone}`}
            color="info"
            variant="contained"
            size="small"
            startIcon={<Call />}
          >
            {translate("contactsPage.control.call")}
          </Button>
          <Button
            href={`https://wa.me/${contactPhone}`}
            size="small"
            color="primary"
            target="_blank"
            variant="contained"
            startIcon={<WhatsApp />}
          >
            {translate("contactsPage.control.whatsApp")}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
